import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { message } from "antd";
import { Card } from "../../../ui/primitives/Card";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import { Button } from "../../../ui/primitives/Button";
import { Input } from "../../../ui/primitives/Input";
import { Select } from "../../../ui/primitives/Select";
import { Tag } from "../../../ui/primitives/Tag";
import { Table } from "../../../ui/primitives/Table";
import { RadioGroup } from "../../../ui/primitives/Radio";
import { Statistic } from "../../../ui/primitives/Statistic";
import { useTokens } from "../../../core/theme/TokensProvider";
import { fromDateOnly, toDateOnly } from "../../../utils/DateOnly";
import { useBdOrganisationService } from "../../../services/members/BdOrganisationServiceProvider";
import { useStaffUserService } from "../../../services/members/StaffUserServiceProvider";
import { useLeadOwnershipFilter } from "../../../common/crm/useLeadOwnershipFilter";
import { StaffUser } from "../../../model/members/StaffUser";
import {
  BD_CATEGORIES,
  BdOrganisation,
  BdOrganisationStatus,
  SaveBdOrganisationRequest,
} from "../../../model/members/BdOrganisation";
import BdOrganisationPanel from "./BdOrganisationPanel";

// Tone per pipeline stage, so the list reads at a glance. Won is the closed-won end of the
// pipeline; Cold is explicitly not a failure state to alarm about, just parked.
const STATUS_TONES: Record<BdOrganisationStatus, "default" | "primary" | "success" | "warning"> = {
  New: "default",
  Contacted: "primary",
  Interested: "warning",
  Cold: "default",
  Won: "success",
};

// fromDateOnly, not bare dayjs(): DateReviver turns the API's YYYY-MM-DD into midnight
// UTC, and reading that with local calendar fields rolls the day back anywhere west of
// Greenwich — showing the wrong date and, worse, writing the wrong one back on save.
const asDate = (value: string | Date | null): dayjs.Dayjs | null => {
  if (!value) return null;
  const d = fromDateOnly(value);
  return d != null && d.isValid() ? d : null;
};

// Encoded either side of the @ rather than whole: the address is tenant-entered free text,
// so a stored "?subject=" must not become mailto parameters — but percent-encoding the @
// itself leaves an address with no domain, which some clients refuse outright.
const mailtoHref = (email: string): string => {
  const at = email.lastIndexOf("@");
  if (at <= 0) return `mailto:${encodeURIComponent(email)}`;
  return `mailto:${encodeURIComponent(email.slice(0, at))}@${encodeURIComponent(email.slice(at + 1))}`;
};

const formatDate = (value: string | Date | null): string => {
  const d = asDate(value);
  return d ? d.format("DD/MM/YYYY") : "—";
};

// signed_off_at is a TIMESTAMPTZ — a real instant, not a calendar date — so it must NOT go
// through asDate/fromDateOnly above. That helper rebuilds from UTC calendar fields, which is
// right for a DATE column and wrong here: a sign-off at 00:30 BST is 23:30Z the day before
// and would render as yesterday. Plain dayjs() reads the instant in the browser's own zone.
const formatTimestamp = (value: string | Date | null): string => {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : "—";
};

/** Due today or overdue — the same predicate Staff Daily's block counts. */
const isDue = (organisation: BdOrganisation): boolean => {
  const due = asDate(organisation.followUpDate);
  return due != null && !due.startOf("day").isAfter(dayjs().startOf("day"));
};

/**
 * Business Development CRM (FORT-362) — the organisations and partnerships that generate
 * future members.
 *
 * Deliberately has no per-record access scoping, unlike the Membership CRM: every logged-in
 * user sees every organisation and may add one. The Mine/All toggle is a lens over the same
 * list, defaulting to All, and exists mainly so Staff Daily can deep-link someone to their
 * own due follow-ups.
 */
const BusinessDevelopmentScreen: React.FC = () => {
  const service = useBdOrganisationService();
  const staffUserService = useStaffUserService();
  const tokens = useTokens();
  // FORT-376: the landing filter and the toggle, shared with the Membership CRM. The two
  // screens narrow by different mechanisms — Membership asks the API, this one filters the
  // tenant-wide list in the browser (the FORT-362 ruling) — but the question of WHOSE leads
  // someone should land on is identical, so the rule lives in one place.
  const { showMineOnly, chooseFilter, mineStaffId, identitySettled } = useLeadOwnershipFilter();

  const [organisations, setOrganisations] = useState<BdOrganisation[]>([]);
  // Counted in SQL over the whole tenant set rather than from the list, so the row stays
  // true if the read is ever capped.
  const [stats, setStats] = useState({ totalCount: 0, dueCount: 0, activeCount: 0, wonCount: 0 });
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<BdOrganisation | null>(null);
  const [saving, setSaving] = useState(false);
  // FORT-374: which row's sign-off is in flight, so only that button spins.
  const [signingOffId, setSigningOffId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgs, staff] = await Promise.all([
        service.getOrganisations(),
        // Secondary: the assignee dropdown degrades to empty rather than failing the screen.
        staffUserService.getStaffUsers().catch(() => [] as StaffUser[]),
      ]);
      setOrganisations(orgs.organisations);
      setStats({
        totalCount: orgs.totalCount,
        dueCount: orgs.dueCount,
        activeCount: orgs.activeCount,
        wonCount: orgs.wonCount,
      });
      setStaffUsers(staff);
    } catch (error) {
      console.error("Error fetching BD organisations:", error);
      message.error("Failed to load organisations");
    } finally {
      setLoading(false);
    }
  }, [service, staffUserService]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return organisations.filter((o) => {
      if (categoryFilter && o.category !== categoryFilter) return false;
      // FORT-368: Mine is created-by-me OR assigned-to-me. Assignment alone left a record
      // you added and then handed on out of your own filter; creation alone would drop one
      // handed TO you. Either relationship makes it yours to work.
      // `=== true` only: null means the landing filter hasn't settled yet, and treating that
      // as Mine would blink the list down and back up as the tier resolves.
      if (
        showMineOnly === true &&
        (mineStaffId == null ||
          (o.assignedStaffId !== mineStaffId && o.createdByStaffId !== mineStaffId))
      ) {
        return false;
      }
      if (!term) return true;
      return (
        o.name.toLowerCase().includes(term) ||
        (o.contactName ?? "").toLowerCase().includes(term) ||
        (o.email ?? "").toLowerCase().includes(term)
      );
    });
  }, [organisations, searchText, categoryFilter, showMineOnly, mineStaffId]);

  const save = useCallback(
    async (request: SaveBdOrganisationRequest) => {
      setSaving(true);
      try {
        await service.saveOrganisation(request);
        message.success(request.id == null ? "Organisation added" : "Organisation updated");
        setPanelOpen(false);
        setEditing(null);
        await fetchData();
      } catch (error) {
        console.error("Error saving BD organisation:", error);
        message.error("Failed to save organisation");
      } finally {
        setSaving(false);
      }
    },
    [service, fetchData]
  );

  /**
   * FORT-374: sign a due follow-up off from the CRM itself.
   *
   * FORT-367 built this action but wired the button only into Staff Daily, so a staff member
   * who opened the CRM directly had no way to mark anything finished. Same endpoint, same
   * assignee guard — this is the missing surface, not a second rule.
   *
   * Preferred over "Log contact" wherever it applies, because it does everything that save
   * does (stamps today, clears the date) *and* records who dealt with it. Log contact
   * remains for a colleague's row, which sign-off would refuse with a 404.
   *
   * Note on an UNASSIGNED organisation this also claims it: SignOffOrganisation sets
   * `assigned_staff_id = COALESCE(assigned_staff_id, @StaffId)` so an orphaned record can't
   * become permanently unsignable (FORT-367). Signing one off therefore makes you its
   * assignee — which is the truthful record of who dealt with it, not a side effect to hide.
   */
  const signOff = useCallback(
    async (organisation: BdOrganisation) => {
      setSigningOffId(organisation.id);
      try {
        await service.signOffOrganisation(organisation.id);
        message.success("Signed off");
        await fetchData();
      } catch (error) {
        console.error("Error signing off BD organisation:", error);
        message.error("Failed to sign off follow-up");
      } finally {
        setSigningOffId(null);
      }
    },
    [service, fetchData]
  );

  // Dealing with a due follow-up is the normal save: stamp today as last-contacted and clear
  // the date. There is no separate done flag — a follow-up is outstanding until the record
  // says otherwise, which is what keeps Staff Daily's count honest.
  const logContact = useCallback(
    (organisation: BdOrganisation) => {
      save({
        id: organisation.id,
        name: organisation.name,
        category: organisation.category,
        contactName: organisation.contactName,
        phone: organisation.phone,
        email: organisation.email,
        status: organisation.status,
        assignedStaffId: organisation.assignedStaffId,
        lastContactedAt: toDateOnly(dayjs()) ?? null,
        followUpDate: null,
        notes: organisation.notes,
      });
    },
    [save]
  );

  const columns = useMemo(
    () => [
      {
        title: "Organisation",
        key: "name",
        render: (_: unknown, o: BdOrganisation) => (
          <span data-testid={`bd-row-${o.id}`} style={{ fontWeight: tokens.fontWeight.medium }}>
            {o.name}
          </span>
        ),
      },
      {
        title: "Category",
        key: "category",
        render: (_: unknown, o: BdOrganisation) =>
          o.category ? <Tag tone="default">{o.category}</Tag> : "—",
      },
      { title: "Contact", key: "contact", render: (_: unknown, o: BdOrganisation) => o.contactName ?? "—" },
      { title: "Phone", key: "phone", render: (_: unknown, o: BdOrganisation) => o.phone ?? "—" },
      {
        title: "Email",
        key: "email",
        render: (_: unknown, o: BdOrganisation) =>
          o.email ? (
            // A live mailto rather than text to copy out. It opens whatever mail client the
            // device is signed into — the app sends nothing itself and logs nothing.
            <a
              href={mailtoHref(o.email)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`bd-email-${o.id}`}
            >
              {o.email}
            </a>
          ) : (
            "—"
          ),
      },
      {
        title: "Status",
        key: "status",
        render: (_: unknown, o: BdOrganisation) => (
          <Tag tone={STATUS_TONES[o.status]} data-testid={`bd-status-${o.id}`}>
            {o.status}
          </Tag>
        ),
      },
      {
        title: "Assigned to",
        key: "assigned",
        render: (_: unknown, o: BdOrganisation) => o.assignedStaffName ?? "—",
      },
      {
        title: "Last contacted",
        key: "lastContacted",
        render: (_: unknown, o: BdOrganisation) => formatDate(o.lastContactedAt),
      },
      {
        title: "Follow-up",
        key: "followUp",
        render: (_: unknown, o: BdOrganisation) => {
          const due = asDate(o.followUpDate);
          if (!due) return "—";
          const today = dayjs().startOf("day");
          if (due.startOf("day").isBefore(today))
            return (
              <Tag tone="danger" data-testid={`bd-overdue-${o.id}`}>
                Overdue
              </Tag>
            );
          if (due.startOf("day").isSame(today))
            return (
              <Tag tone="warning" data-testid={`bd-due-today-${o.id}`}>
                Today
              </Tag>
            );
          return due.format("DD/MM/YYYY");
        },
      },
      {
        // FORT-374: the audit pair FORT-367 recorded but never showed anywhere. Without it
        // a signed-off organisation is indistinguishable from one that simply has no
        // follow-up booked — both render "—" under Follow-up.
        title: "Signed off",
        key: "signedOff",
        render: (_: unknown, o: BdOrganisation) => {
          if (!o.signedOffAt) return "—";
          return (
            <Text color="secondary" data-testid={`bd-signed-off-${o.id}`}>
              {formatTimestamp(o.signedOffAt)}
              {o.signedOffByStaffName ? ` — ${o.signedOffByStaffName}` : ""}
            </Text>
          );
        },
      },
      {
        title: "",
        key: "actions",
        render: (_: unknown, o: BdOrganisation) => {
          if (!isDue(o)) return null;
          // Nothing until we know who is asking. Rendering the fallback first and swapping it
          // a moment later puts a different action under a cursor already moving towards it.
          //
          // Gated on identitySettled, NOT on showMineOnly: arriving with ?mine=0 or ?mine=1
          // decides the filter from the URL before /dashboard/me has answered, so the filter
          // is a false proxy for "we know who this is" on exactly the path the toggle writes.
          if (!identitySettled) return null;
          // Sign-off is assignee-scoped server-side and also claims an unassigned row, so
          // offer it exactly where the write would succeed. Anywhere else — a colleague's
          // organisation, or no resolved staff identity at all — the button would 404, so
          // the row keeps the unguarded "Log contact" instead of a control that can't work.
          const canSignOff =
            mineStaffId != null && (o.assignedStaffId == null || o.assignedStaffId === mineStaffId);
          return canSignOff ? (
            <Button
              size="sm"
              variant="primary"
              loading={signingOffId === o.id}
              disabled={signingOffId != null && signingOffId !== o.id}
              onClick={(e?: React.MouseEvent) => {
                e?.stopPropagation();
                signOff(o);
              }}
              data-testid={`bd-sign-off-${o.id}`}
            >
              Sign off
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={(e?: React.MouseEvent) => {
                e?.stopPropagation();
                logContact(o);
              }}
              data-testid={`bd-log-contact-${o.id}`}
            >
              Log contact
            </Button>
          );
        },
      },
    ],
    [tokens, logContact, signOff, signingOffId, mineStaffId, identitySettled]
  );

  return (
    <div style={{ padding: tokens.space[4] }} data-testid="bd-screen">
      <Stack
        direction="horizontal"
        gap={3}
        align="center"
        style={{ justifyContent: "space-between", marginBottom: tokens.space[4] }}
      >
        <Stack gap={0}>
          <Text variant="h2">BUSINESS DEVELOPMENT CRM</Text>
          <Text color="secondary">
            Organisations and partnerships that generate future members
          </Text>
        </Stack>
        <Button
          variant="primary"
          onClick={() => {
            setEditing(null);
            setPanelOpen(true);
          }}
          data-testid="bd-add-organisation"
        >
          Add organisation
        </Button>
      </Stack>

      <Stack direction="horizontal" gap={3} wrap style={{ marginBottom: tokens.space[4] }}>
        <Card style={{ flex: 1, minWidth: 180 }}>
          <Statistic title="Total organisations" value={stats.totalCount} data-testid="bd-kpi-total" />
        </Card>
        <Card style={{ flex: 1, minWidth: 180 }}>
          <Statistic
            title="Follow-ups due"
            value={stats.dueCount}
            tone="warning"
            data-testid="bd-kpi-due"
          />
        </Card>
        <Card style={{ flex: 1, minWidth: 180 }}>
          <Statistic
            title="Active opportunities"
            value={stats.activeCount}
            data-testid="bd-kpi-active"
          />
        </Card>
        <Card style={{ flex: 1, minWidth: 180 }}>
          <Statistic title="Won partnerships" value={stats.wonCount} data-testid="bd-kpi-won" />
        </Card>
      </Stack>

      <Card style={{ marginBottom: tokens.space[3] }}>
        <Stack direction="horizontal" gap={3} wrap align="center">
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search organisations…"
              data-testid="bd-search"
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <Select
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v as string | undefined)}
              options={BD_CATEGORIES.map((c) => ({ label: c, value: c }))}
              placeholder="All categories"
              allowClear
              data-testid="bd-category-filter"
            />
          </div>
          <RadioGroup
            value={showMineOnly ? "mine" : "all"}
            onChange={(v) => chooseFilter(v === "mine")}
            // Same wording as the Membership CRM: two CRMs side by side saying "Mine" in one
            // and "My Leads" in the other read as two unrelated features.
            options={[
              { label: "All Leads", value: "all" },
              { label: "My Leads", value: "mine" },
            ]}
            optionType="button"
            data-testid="bd-mine-toggle"
          />
          <Button onClick={fetchData} data-testid="bd-refresh">
            Refresh
          </Button>
        </Stack>
      </Card>

      <Card>
        <Table<BdOrganisation>
          columns={columns}
          dataSource={filtered}
          rowKey={(o) => o.id}
          // Undecided counts as loading: without it the full tenant list paints first and
          // blinks DOWN to the user's own rows the moment the tier resolves, which for a
          // non-manager is the common case rather than the edge one.
          loading={loading || showMineOnly === null}
          size="small"
          scroll={{ x: true }}
          onRow={(o) => ({
            onClick: () => {
              setEditing(o);
              setPanelOpen(true);
            },
            style: { cursor: "pointer" },
          })}
          data-testid="bd-table"
        />
      </Card>

      <BdOrganisationPanel
        open={panelOpen}
        organisation={editing}
        staffUsers={staffUsers}
        saving={saving}
        onClose={() => {
          setPanelOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />
    </div>
  );
};

export default BusinessDevelopmentScreen;
